import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { VehicleForm, triplePSDatasource } from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { Vehicle } from "../../models";
import { logout } from "../Auth/authSlice";

export interface VehicleState {
  getVehicles: APIResult<Vehicle[]>;
  getVehicle: APIResult<Vehicle>;
  createVehicle: APIResult<Vehicle>;
  updateVehicle: APIResult<Vehicle>;
  deleteVehicle: APIResult<string>;

  vehicles: Vehicle[];
  lastUpdatedAt?: string;
  selectedVehicle?: Vehicle;
  showDeleteVehicleConfirmationModal?: Vehicle;
  vehicleToRemove?: string;
  showAddVehicleModal: boolean;
  showEditVehicleModal: boolean;
}

const initialState: VehicleState = {
  getVehicles: emptyAPIResult(),
  getVehicle: emptyAPIResult(),
  createVehicle: emptyAPIResult(),
  updateVehicle: emptyAPIResult(),
  deleteVehicle: emptyAPIResult(),

  vehicles: [],
  showAddVehicleModal: false,
  showEditVehicleModal: false,
};

export const getVehicles = createAsyncThunk(
  "vehicles/getVehicles",
  async (args: { withStats?: boolean } = {}) => {
    return triplePSDatasource.getVehicles(args.withStats ?? false);
  }
);

export const getVehicle = createAsyncThunk(
  "vehicles/getVehicle",
  async (id: string) => {
    return triplePSDatasource.getVehicle(id);
  }
);

export const createVehicle = createAsyncThunk(
  "vehicles/createVehicle",
  async (form: VehicleForm) => {
    return triplePSDatasource.createVehicle(form);
  }
);

export const updateVehicle = createAsyncThunk(
  "vehicles/updateVehicle",
  async (vehicle: Vehicle) => {
    return triplePSDatasource.updateVehicle(vehicle);
  }
);

export const deleteVehicle = createAsyncThunk(
  "vehicles/deleteVehicle",
  async (id: string) => {
    return triplePSDatasource.deleteVehicle(id);
  }
);

export const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    setVehicleToRemove(state, action) {
      state.vehicleToRemove = action.payload;
    },
    setSelectedVehicle(state, action) {
      state.selectedVehicle = action.payload;
    },
    toggleShowAddVehicleModal(state) {
      state.showAddVehicleModal = !state.showAddVehicleModal;
    },
    toggleShowEditVehicleModal(state) {
      state.showEditVehicleModal = !state.showEditVehicleModal;
    },
    showDeleteVehicleConfirmationModal(state, action) {
      state.showDeleteVehicleConfirmationModal = action.payload;
    },
    resetShowDeleteVehicleConfirmationModal(state) {
      state.showDeleteVehicleConfirmationModal = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVehicles.pending, (state) => {
        state.getVehicles = loadingAPIResult();
      })
      .addCase(getVehicles.fulfilled, (state, action) => {
        state.getVehicles = successAPIResult(action.payload);
        state.vehicles = action.payload;
        state.lastUpdatedAt = new Date().toISOString();
      })
      .addCase(getVehicles.rejected, (state, action) => {
        state.getVehicles = errorAPIResult(action.error);
      })
      .addCase(getVehicle.pending, (state) => {
        state.getVehicle = loadingAPIResult();
      })
      .addCase(getVehicle.fulfilled, (state, action) => {
        state.getVehicle = successAPIResult(action.payload);
        state.selectedVehicle = action.payload;
      })
      .addCase(getVehicle.rejected, (state, action) => {
        state.getVehicle = errorAPIResult(action.error);
      })
      .addCase(createVehicle.pending, (state) => {
        state.createVehicle = loadingAPIResult();
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.createVehicle = successAPIResult(action.payload);
        state.vehicles.push(action.payload);
        state.showAddVehicleModal = false;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.createVehicle = errorAPIResult(action.error);
      })
      .addCase(updateVehicle.pending, (state) => {
        state.updateVehicle = loadingAPIResult();
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.updateVehicle = successAPIResult(action.payload);
        const index = state.vehicles.findIndex(
          (v) => v.id === action.payload.id
        );
        if (index !== -1) {
          // The PUT response has no location/stats (db:"-" — only a with_stats
          // GET returns them). Merge so an edit doesn't blank the telemetry
          // cells / map pin until the next poll refetches.
          const existing = state.vehicles[index];
          state.vehicles[index] = {
            ...action.payload,
            location: action.payload.location ?? existing.location,
            stats: action.payload.stats ?? existing.stats,
          };
        }
        state.selectedVehicle = action.payload;
        state.showEditVehicleModal = false;
      })
      .addCase(updateVehicle.rejected, (state, action) => {
        state.updateVehicle = errorAPIResult(action.error);
      })
      .addCase(deleteVehicle.pending, (state) => {
        state.deleteVehicle = loadingAPIResult();
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.deleteVehicle = successAPIResult(action.payload);
        state.vehicles = state.vehicles
          ? state.vehicles.filter((v) => v.id !== action.payload)
          : [];
        state.vehicleToRemove = undefined;
        state.showDeleteVehicleConfirmationModal = undefined;
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.deleteVehicle = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getVehicles = initialState.getVehicles;
        state.getVehicle = initialState.getVehicle;
        state.createVehicle = initialState.createVehicle;
        state.updateVehicle = initialState.updateVehicle;
        state.deleteVehicle = initialState.deleteVehicle;

        state.vehicles = initialState.vehicles;
        state.lastUpdatedAt = undefined;
        state.selectedVehicle = undefined;
        state.vehicleToRemove = undefined;
        state.showAddVehicleModal = initialState.showAddVehicleModal;
        state.showEditVehicleModal = initialState.showEditVehicleModal;
        state.showDeleteVehicleConfirmationModal = undefined;
      });
  },
});

export const {
  setVehicleToRemove,
  setSelectedVehicle,
  toggleShowAddVehicleModal,
  toggleShowEditVehicleModal,
  showDeleteVehicleConfirmationModal,
  resetShowDeleteVehicleConfirmationModal,
} = vehiclesSlice.actions;

export default vehiclesSlice.reducer;
